import { useEffect, useState } from "react";
import { plus } from "../../asset";
import { ExpandableSection } from "../../components";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import PageHeader from "../../components/PageHeader";
import { useLocation, useParams } from "react-router-dom";
import FormApis from "../../api/formApis";
import { toast } from "sonner";
import AddEditQuestions from "../query-management-setup/AddEditQuestions";
import useConfirmToast from "../../util/hooks/useConfirmToast";

// openForms[sectionId] = questionId | "NEW" | null

type OpenFormsMap = {
  [sectionId: string]: string | "NEW" | null;
};

export default function AddForm() {
  const { formId } = useParams();
  const { state } = useLocation();
  const formName = state?.formName;
  const formSeqId = state?.formSeqId;

  const [sections, setSections] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [showSectionInput, setShowSectionInput] = useState(false);
  const [openForms, setOpenForms] = useState<OpenFormsMap>({});
  const [loadingSection, setLoadingSection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState<any>(null);
  const { confirm, modal } = useConfirmToast();

  useEffect(() => {
    fetchFormData();
  }, [formSeqId]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      const result = await FormApis.getFormBySeqId(formSeqId!);

      if (result?.status === 1) {
        const transformedSections = result.data.formSections.map((section) => ({
          id: section.formSectionId,
          name: section.sectionName,
          questions: section.formQuestions.map((q) => ({
            id: q.questionId,
            title: q.question,
            type: q.questionType,
            choices: q.options || [],
            required: q.isMandatory,
          })),
        }));
        setSections(transformedSections);
      }
    } catch {
      toast.error("Failed to load form data");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Sections ---------------- */

  const addSection = async () => {
    if (!name.trim()) return;

    setLoadingSection(true);
    try {
      if (editingSectionId) {
        await FormApis.updateSection(editingSectionId, name.trim());
        toast.success("Section updated!");
      } else {
        await FormApis.createSection(formId!, name.trim());
        toast.success("Section created!");
      }
      await fetchFormData();
    } finally {
      setLoadingSection(false);
      setName("");
      setShowSectionInput(false);
      setEditingSectionId(null);
    }
  };

  const deleteSection = async (id: string) => {
    const result = await confirm(
      "Are You sure You want to delete this section"
    );
    if (!result) return;
    await FormApis.deleteSection(id);
    await fetchFormData();
    toast.success("Section deleted!");
  };

  const editSection = (id) => {
    const section = sections.find((s) => s.id === id);
    if (!section) return;
    setName(section.name);
    setEditingSectionId(id);
    setShowSectionInput(true);
  };

  /* ---------------- Questions ---------------- */

  const openAddQuestion = (sectionId: string) => {
    setEditingQuestion(null);
    setOpenForms({ [sectionId]: "NEW" }); // close others
  };

  const openEditQuestion = (sectionId: string, question: any) => {
    setEditingQuestion({ ...question, sectionId });
    setOpenForms({ [sectionId]: question.id }); // close others
  };

  const closeQuestionForm = () => {
    setEditingQuestion(null);
    setOpenForms({});
  };

  const handleSaveQuestion = async (sectionId: string, data: any) => {
    const payload = {
      questionName: data.title,
      marks: 0,
      question: data.title,
      isMandatory: data.required,
      options:
        data.type === "CHOICE" || data.type === "MULTIPLE_CHOICE"
          ? data.choices
          : [],
      questionType: data.type,
    };

    if (editingQuestion) {
      await FormApis.updateQuestion(editingQuestion.id, payload);
      toast.success("Question updated!");
    } else {
      await FormApis.createQuestion(sectionId, payload);
      toast.success("Question added!");
    }

    await fetchFormData();
    closeQuestionForm();
  };

  const deleteQuestion = async (questionId: number) => {
    const result = await confirm(
      "Are You sure You want to remove this question"
    );
    if (!result) return;
    await FormApis.deleteQuestion(questionId);
    await fetchFormData();
  };

  return (
    <div className="flex flex-col gap-3">
      <PageHeader title={formName} showBack />

      <div className="bg-white px-4 md:px-8 py-5 rounded-lg flex flex-col gap-3">
        <h3 className="text-lg font-semibold text-neutral-400">
          Form Sections
        </h3>

        <div className="flex flex-col gap-4">
          {sections.map((section, index) => (
            <div key={section.id}>
              {editingSectionId === section.id ? (
                <div className="flex flex-col gap-6 p-4 border rounded-lg">
                  <CustomInput
                    name="sectionName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    label="Section name"
                  />
                  <div className="flex justify-end gap-2 text-xs">
                    <button
                      className="bg-primary-500 rounded-full px-4 py-2 text-white"
                      onClick={addSection}
                      disabled={loadingSection}
                    >
                      Update
                    </button>
                    <button
                      className="bg-neutral-200 text-white rounded-full px-4 py-2"
                      onClick={() => {
                        setEditingSectionId(null);
                        setName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // Normal section layout
                <ExpandableSection
                  section={section}
                  onEdit={() => editSection(section.id)}
                  onDelete={() => deleteSection(section.id)}
                  isFirst={index === 0}
                >
                  {section.questions.map((question) => (
                    <div
                      key={question.id}
                      className="md:px-8 px-4 py-4 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-neutral-700">
                            {question.title}
                          </h4>
                          <span className="text-sm text-neutral-400">
                            Required: {question.required ? "Yes" : "No"}
                          </span>
                          <p className="text-xs text-neutral-400 mt-1">
                            Type: {question.type}
                          </p>

                          {openForms[section.id] === question.id && (
                            <div className="mt-4 p-4 rounded-lg border">
                              <AddEditQuestions
                                mode="FORM"
                                editingData={editingQuestion}
                                onSave={(data) =>
                                  handleSaveQuestion(section.id, data)
                                }
                                onClose={closeQuestionForm}
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col md:flex-row gap-3">
                          <button
                            className="bg-warning-500 rounded-full px-4 py-2 text-xs font-semibold text-white"
                            onClick={() =>
                              openEditQuestion(section.id, question)
                            }
                          >
                            Edit
                          </button>
                          <button
                            className="bg-error-500 rounded-full px-4 py-2 text-xs font-semibold text-white"
                            onClick={() => deleteQuestion(question.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {openForms[section.id] === "NEW" && (
                    <div className="mt-4">
                      <AddEditQuestions
                        mode="FORM"
                        onSave={(data) => handleSaveQuestion(section.id, data)}
                        onClose={closeQuestionForm}
                      />
                    </div>
                  )}

                  {openForms[section.id] == null && (
                    <div className="flex justify-end mt-2">
                      <CustomButton
                        color="bg-warning-600"
                        text="Add Question"
                        icon={plus}
                        onClick={() => openAddQuestion(section.id)}
                      />
                    </div>
                  )}
                </ExpandableSection>
              )}
            </div>
          ))}
        </div>

        {showSectionInput && editingSectionId === null && (
          <div className="flex flex-col gap-6 p-4 border rounded-lg">
            <CustomInput
              name="sectionName"
              value={name}
              placeholder="Enter Section Name"
              onChange={(e) => setName(e.target.value)}
              label="Section name"
            />
            <div className="flex justify-end gap-2 text-xs">
              <button
                className="bg-primary-500 rounded-full px-4 py-2 text-white"
                onClick={addSection}
                disabled={loadingSection}
              >
                Save
              </button>
              <button
                className="bg-neutral-200 text-white rounded-full px-4 py-2"
                onClick={() => {
                  setShowSectionInput(false);
                  setName("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!showSectionInput && (
          <CustomButton
            color="bg-success-600"
            text="Add Section"
            icon={plus}
            onClick={() => setShowSectionInput(true)}
          />
        )}
      </div>
      {modal}
    </div>
  );
}
